import React, { useCallback, useEffect, useState } from 'react'
import { ledgerApi, LedgerResult, LedgerEntryRow } from '../services/api'
import { Loader, Modal } from '../components/UI'
import { formatToman, formatDateTimeFa, toFaDigits } from '../utils/format'
import toast from 'react-hot-toast'

const STATUS_FA: Record<string, string> = {
  PENDING: 'در انتظار',
  CONFIRMED: 'تایید شده',
  PROCESSING: 'در حال پردازش',
  COMPLETED: 'تکمیل شده',
  CANCELLED: 'لغو شده'
}

const KIND_LABEL: Record<string, string> = {
  BUY: 'خرید مشتری',
  SELL: 'فروش مشتری',
  MANUAL: 'ثبت دستی'
}

const KIND_STYLE: Record<string, string> = {
  BUY: 'bg-green-100 text-green-800',
  SELL: 'bg-gold-100 text-gold-800',
  MANUAL: 'bg-gray-200 text-gray-700'
}

const num = (v: number) => (v > 0 ? `+${toFaDigits(v)}` : toFaDigits(v))

/**
 * صفحه «تراز»
 * در هر لحظه مشخص می‌کند مثبت هستید (پول دستتان است) یا منفی (فلز دستتان است)
 */
const Ledger: React.FC = () => {
  const [data, setData] = useState<LedgerResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [includeCancelled, setIncludeCancelled] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    kind: 'CASH' as 'CASH' | 'METAL',
    metal: 'GOLD' as 'GOLD' | 'SILVER',
    direction: 'IN' as 'IN' | 'OUT',
    amount: 0,
    note: ''
  })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await ledgerApi.get({
        from: from || undefined,
        to: to || undefined,
        includeCancelled: includeCancelled ? 'true' : undefined
      })
      if (res.data.success && res.data.data) setData(res.data.data)
    } catch {
      toast.error('خطا در دریافت تراز')
    } finally {
      setLoading(false)
    }
  }, [from, to, includeCancelled])

  useEffect(() => {
    load()
  }, [load])

  const submitEntry = async () => {
    if (!(form.amount > 0)) {
      toast.error('مقدار باید بزرگ‌تر از صفر باشد')
      return
    }
    setSaving(true)
    try {
      const res = await ledgerApi.createEntry({
        kind: form.kind,
        metal: form.kind === 'METAL' ? form.metal : undefined,
        direction: form.direction,
        amount: form.amount,
        note: form.note || undefined
      })
      if (res.data.success) {
        toast.success('ردیف تراز ثبت شد')
        setModalOpen(false)
        setForm({ kind: 'CASH', metal: 'GOLD', direction: 'IN', amount: 0, note: '' })
        load()
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'خطا در ثبت ردیف تراز')
    } finally {
      setSaving(false)
    }
  }

  const removeEntry = async (row: LedgerEntryRow) => {
    if (row.source !== 'MANUAL') return
    try {
      await ledgerApi.deleteEntry(row.id)
      toast.success('ردیف حذف شد')
      load()
    } catch {
      toast.error('خطا در حذف ردیف')
    }
  }

  if (loading && !data) return <div className="py-20 flex justify-center"><Loader /></div>
  if (!data) return <div className="py-20 text-center text-gray-500">تراز در دسترس نیست</div>

  const s = data.summary
  const positive = s.position === 'POSITIVE'
  const gold = s.metals.find((m) => m.metal === 'GOLD')
  const silver = s.metals.find((m) => m.metal === 'SILVER')

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-900">تراز</h1>
          <p className="text-sm text-gray-500 mt-1">
            وضعیت لحظه‌ای صندوق و موجودی فلز؛ مثبت یعنی پول دست شماست، منفی یعنی فلز دست شماست
          </p>
        </div>
        <button className="btn btn-outline" onClick={() => setModalOpen(true)}>
          + ثبت دستی (موجودی اولیه / واریز / برداشت)
        </button>
      </div>

      {/* کارت اصلی تراز */}
      <div
        className={`card p-6 border-2 ${
          positive ? 'border-green-300 bg-green-50' : 'border-gold-300 bg-gold-50'
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="text-xs text-gray-500 mb-1">تراز کل</div>
            <div className={`text-3xl font-black ${positive ? 'text-green-700' : 'text-gold-800'}`}>
              {formatToman(Math.abs(s.balance))}
            </div>
            <div className={`mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-black ${
              positive ? 'bg-green-600 text-white' : 'bg-gold-600 text-white'
            }`}>
              {positive ? 'مثبت — پول بیشتری دست شماست' : 'منفی — فلز بیشتری دست شماست'}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 min-w-[150px]">
              <div className="text-[11px] text-gray-500">نقد (فروش − خرید)</div>
              <div className={`font-black mt-0.5 ${s.cashNet >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                {formatToman(s.cashNet)}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 min-w-[150px]">
              <div className="text-[11px] text-gray-500">موجودی فلز (وزن)</div>
              <div className="font-black mt-0.5 text-gray-900">{toFaDigits(s.netGrams)} گرم</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 min-w-[150px]">
              <div className="text-[11px] text-gray-500">ارزش روز فلز</div>
              <div className="font-black mt-0.5 text-gray-900">{formatToman(s.metalValue)}</div>
            </div>
          </div>
        </div>

        <p className="text-xs text-gray-500 mt-4 leading-6">
          محاسبه: <b>تراز = نقد − ارزش روزِ فلزِ موجود</b>. اگر مبلغ فروش بیشتر از خرید باشد و پول در صندوق مانده باشد،
          تراز مثبت است؛ اگر پول به فلز تبدیل شده باشد، تراز منفی می‌شود.
          {s.priceSource === 'unavailable' && ' (قیمت روز فلز در دسترس نبود؛ ارزش فلز با قیمت صفر محاسبه شده است.)'}
        </p>
      </div>

      {/* تفکیک طلا و نقره */}
      <div className="grid md:grid-cols-2 gap-5">
        {[
          { m: gold, label: 'طلا', color: 'bg-gold-600' },
          { m: silver, label: 'نقره', color: 'bg-gray-600' }
        ].map(({ m, label, color }) => (
          <div key={label} className="card overflow-hidden">
            <div className={`${color} text-white px-5 py-3 font-black`}>تراز {label}</div>
            <div className="p-5 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">فروش (گرم / مبلغ)</span>
                <span className="font-bold">
                  {toFaDigits(m?.soldGrams || 0)} گرم — {formatToman(m?.soldAmount || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">خرید (گرم / مبلغ)</span>
                <span className="font-bold">
                  {toFaDigits(m?.boughtGrams || 0)} گرم — {formatToman(m?.boughtAmount || 0)}
                </span>
              </div>
              <div className="flex justify-between border-t border-gray-100 pt-3">
                <span className="text-gray-600">نقد این فلز</span>
                <span className={`font-black ${(m?.cashNet || 0) >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                  {formatToman(m?.cashNet || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">موجودی خالص</span>
                <span className="font-black">{toFaDigits(m?.netGrams || 0)} گرم</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">قیمت روز هر گرم</span>
                <span className="font-bold">{formatToman(m?.pricePerGram || 0)}</span>
              </div>
              <div className="flex justify-between items-center border-t border-gray-100 pt-3">
                <span className="text-gray-600">وضعیت</span>
                <span className={`badge ${m?.position === 'POSITIVE' ? 'bg-green-100 text-green-800' : 'bg-gold-100 text-gold-800'}`}>
                  {m?.position === 'POSITIVE' ? 'مثبت (پول)' : 'منفی (فلز)'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* آمار کلی */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="text-xs text-gray-500">کل سفارش‌های خرید مشتری</div>
          <div className="font-black text-lg mt-0.5">{toFaDigits(s.buyOrderCount)} سفارش</div>
          <div className="text-xs text-gray-500 mt-1">{formatToman(s.totalSoldAmount)}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gray-500">کل سفارش‌های فروش مشتری</div>
          <div className="font-black text-lg mt-0.5">{toFaDigits(s.sellOrderCount)} سفارش</div>
          <div className="text-xs text-gray-500 mt-1">{formatToman(s.totalBoughtAmount)}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gray-500">مجموع وزن فروخته‌شده</div>
          <div className="font-black text-lg mt-0.5">{toFaDigits(s.metals.reduce((a, m) => a + m.soldGrams, 0))} گرم</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-gray-500">مجموع وزن خریداری‌شده</div>
          <div className="font-black text-lg mt-0.5">{toFaDigits(s.metals.reduce((a, m) => a + m.boughtGrams, 0))} گرم</div>
        </div>
      </div>

      {/* فیلترها */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label">از تاریخ</label>
          <input type="date" className="input !w-auto" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="label">تا تاریخ</label>
          <input type="date" className="input !w-auto" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 pb-3">
          <input
            type="checkbox"
            checked={includeCancelled}
            onChange={(e) => setIncludeCancelled(e.target.checked)}
          />
          نمایش سفارش‌های لغوشده
        </label>
        {(from || to) && (
          <button className="btn btn-secondary mb-0.5" onClick={() => { setFrom(''); setTo('') }}>
            حذف فیلتر تاریخ
          </button>
        )}
      </div>

      {/* دفتر کامل معاملات */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-black text-gray-900">دفتر کامل معاملات</h2>
          <span className="text-xs text-gray-500">{toFaDigits(data.entries.length)} ردیف</span>
        </div>
        <div className="table-wrap">
          <table className="table-admin">
            <thead>
              <tr>
                <th>تاریخ</th>
                <th>نوع</th>
                <th>شماره / شرح</th>
                <th>مشتری</th>
                <th className="!text-center">طلا (گرم)</th>
                <th className="!text-center">نقره (گرم)</th>
                <th>مبلغ</th>
                <th>اثر روی نقد</th>
                <th>وضعیت</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.entries.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center text-gray-400 py-10">
                    هنوز معامله‌ای ثبت نشده است
                  </td>
                </tr>
              ) : (
                data.entries.map((e) => (
                  <tr key={e.id}>
                    <td className="text-xs text-gray-500 whitespace-nowrap">{formatDateTimeFa(e.date)}</td>
                    <td>
                      <span className={`badge ${KIND_STYLE[e.kind]}`}>{KIND_LABEL[e.kind]}</span>
                    </td>
                    <td>
                      <div className="font-bold text-gray-800">{e.title}</div>
                      {e.orderNumber && (
                        <div className="text-[11px] text-gray-400" dir="ltr">{e.orderNumber}</div>
                      )}
                      {e.note && <div className="text-[11px] text-gray-400">{e.note}</div>}
                    </td>
                    <td className="text-xs">{e.customer || '—'}</td>
                    <td className={`text-center font-bold ${e.goldGrams < 0 ? 'text-red-600' : e.goldGrams > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                      {e.goldGrams ? num(e.goldGrams) : '—'}
                    </td>
                    <td className={`text-center font-bold ${e.silverGrams < 0 ? 'text-red-600' : e.silverGrams > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                      {e.silverGrams ? num(e.silverGrams) : '—'}
                    </td>
                    <td className="whitespace-nowrap font-bold">{e.amount ? formatToman(e.amount) : '—'}</td>
                    <td className={`whitespace-nowrap font-black ${e.cashDelta > 0 ? 'text-green-700' : e.cashDelta < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {e.cashDelta ? formatToman(e.cashDelta) : '—'}
                    </td>
                    <td className="text-xs">
                      {e.status ? (
                        <span className="badge bg-gray-100 text-gray-700">{STATUS_FA[e.status] || e.status}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {e.source === 'MANUAL' && (
                        <button
                          className="text-xs text-red-500 hover:underline"
                          onClick={() => removeEntry(e)}
                        >
                          حذف
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={modalOpen} title="ثبت دستی ردیف تراز" onClose={() => setModalOpen(false)}>
        <div className="space-y-4">
          <p className="text-xs text-gray-500 leading-6">
            برای ثبت موجودی اولیه، واریز/برداشت نقدی یا افزودن/برداشت فلز از این فرم استفاده کنید.
            این ردیف‌ها در محاسبه تراز لحاظ می‌شوند.
          </p>

          <div>
            <label className="label">نوع</label>
            <select
              className="input"
              value={form.kind}
              onChange={(e) => setForm((p) => ({ ...p, kind: e.target.value as 'CASH' | 'METAL' }))}
            >
              <option value="CASH">نقد (تومان)</option>
              <option value="METAL">فلز (گرم)</option>
            </select>
          </div>

          {form.kind === 'METAL' && (
            <div>
              <label className="label">فلز</label>
              <select
                className="input"
                value={form.metal}
                onChange={(e) => setForm((p) => ({ ...p, metal: e.target.value as 'GOLD' | 'SILVER' }))}
              >
                <option value="GOLD">طلا</option>
                <option value="SILVER">نقره</option>
              </select>
            </div>
          )}

          <div>
            <label className="label">جهت</label>
            <select
              className="input"
              value={form.direction}
              onChange={(e) => setForm((p) => ({ ...p, direction: e.target.value as 'IN' | 'OUT' }))}
            >
              <option value="IN">افزودن / ورود</option>
              <option value="OUT">کاهش / خروج</option>
            </select>
          </div>

          <div>
            <label className="label">
              مقدار {form.kind === 'CASH' ? '(تومان)' : '(گرم)'}
            </label>
            <input
              type="number"
              dir="ltr"
              className="input"
              value={form.amount}
              onChange={(e) => setForm((p) => ({ ...p, amount: parseFloat(e.target.value) || 0 }))}
            />
          </div>

          <div>
            <label className="label">توضیح</label>
            <input
              className="input"
              placeholder="مثلاً موجودی اولیه صندوق"
              value={form.note}
              onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
            />
          </div>

          <div className="flex justify-end gap-2">
            <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>انصراف</button>
            <button className="btn btn-primary" disabled={saving} onClick={submitEntry}>
              {saving ? 'در حال ذخیره…' : 'ثبت ردیف'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default Ledger
